import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    category: 'Division Immobilière',
    published: true
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs?published_only=false`, {
        headers: getAuthHeader()
      });
      const blog = response.data.find(b => b.id === id);
      if (blog) {
        setFormData({
          title: blog.title,
          excerpt: blog.excerpt,
          content: blog.content,
          image_url: blog.image_url || '',
          category: blog.category,
          published: blog.published
        });
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      setError('Article non trouvé');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await axios.put(`${API}/blogs/${id}`, formData, {
          headers: getAuthHeader()
        });
      } else {
        await axios.post(`${API}/blogs`, formData, {
          headers: getAuthHeader()
        });
      }
      navigate('/admin/blogs');
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-edit-page">
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate('/admin/blogs')}
          className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
        >
          <ArrowLeft size={24} className="text-[#1e3a5f]" />
        </button>
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1e3a5f]">
            {isEditing ? 'Modifier l\'Article' : 'Nouvel Article'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditing ? 'Modifiez les informations de l\'article' : 'Créez un nouvel article de blog'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow-sm p-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-[#1e3a5f] font-medium">
                    Titre *
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="rounded-none border-gray-300 focus:border-[#1e3a5f]"
                    placeholder="Titre de l'article"
                    data-testid="blog-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="excerpt" className="text-[#1e3a5f] font-medium">
                    Extrait *
                  </Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="rounded-none border-gray-300 focus:border-[#1e3a5f] resize-none"
                    placeholder="Un court résumé de l'article..."
                    data-testid="blog-excerpt-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content" className="text-[#1e3a5f] font-medium">
                    Contenu *
                  </Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    rows={15}
                    className="rounded-none border-gray-300 focus:border-[#1e3a5f] resize-none font-mono text-sm"
                    placeholder="Contenu de l'article...

Vous pouvez utiliser:
## Titre de section
### Sous-titre
- Liste à puces
1. Liste numérotée
**Texte en gras**"
                    data-testid="blog-content-input"
                  />
                  <p className="text-gray-400 text-xs">
                    Utilisez ## pour les titres, ### pour les sous-titres, - pour les listes
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className="bg-white shadow-sm p-6">
              <h3 className="font-semibold text-[#1e3a5f] mb-4">Publication</h3>
              <div className="flex items-center justify-between">
                <Label htmlFor="published" className="text-gray-700">
                  Publier l'article
                </Label>
                <Switch
                  id="published"
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
                  data-testid="blog-published-switch"
                />
              </div>
              <p className="text-gray-400 text-xs mt-2">
                {formData.published ? 'L\'article sera visible sur le site' : 'L\'article restera en brouillon'}
              </p>
            </div>

            {/* Category */}
            <div className="bg-white shadow-sm p-6">
              <h3 className="font-semibold text-[#1e3a5f] mb-4">Catégorie</h3>
              <div className="space-y-2">
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full rounded-none border border-gray-300 px-3 py-2 focus:border-[#1e3a5f] focus:outline-none"
                  data-testid="blog-category-select"
                >
                  <option value="Division Immobilière">Division Immobilière</option>
                  <option value="Indivision">Indivision</option>
                  <option value="Marchand de Biens">Marchand de Biens</option>
                  <option value="Copropriété">Copropriété</option>
                  <option value="Investissement">Investissement</option>
                  <option value="Actualités">Actualités</option>
                </select>
              </div>
            </div>

            {/* Image */}
            <div className="bg-white shadow-sm p-6">
              <h3 className="font-semibold text-[#1e3a5f] mb-4">Image</h3>
              <div className="space-y-4">
                <Input
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  className="rounded-none border-gray-300 focus:border-[#1e3a5f]"
                  placeholder="URL de l'image"
                  data-testid="blog-image-input"
                />
                {formData.image_url && (
                  <img 
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-32 object-cover border"
                  />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white shadow-sm p-6">
              {error && (
                <p className="text-red-500 text-sm mb-4">{error}</p>
              )}
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none py-3"
                data-testid="blog-save-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full loading-spinner"></div>
                    Enregistrement...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Save size={18} />
                    {isEditing ? 'Mettre à jour' : 'Créer l\'article'}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminBlogEdit;
